/**
 * CRM Site Sync Hook
 *
 * Syncs CRM when a logged-in user lands on a site (homepage login) or switches country site.
 * Registration and /login pages sync on their own; this hook covers all other routes via _app.
 */

import {useEffect, useRef} from 'react';
import {useCustomer, useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useCrm} from '@amblique/king-living/app/contexts/crm-context';
import {useCrmSync} from '@amblique/king-living/app/hooks/use-crm-sync';
import {parseAccountShowroom} from '@amblique/king-living/app/utils/utils';

const getPreferredStoreId = (customer, siteId) => (
  parseAccountShowroom(customer?.c_preferredStoreId, siteId)?.id ?? null
);

export const useCrmSiteSync = () => {
  const {isRegistered} = useCustomerType();
  const customerId = useCustomerId();
  const {data: customer} = useCustomer(
    {parameters: {customerId}},
    {enabled: !!customerId && isRegistered}
  );

  const {site} = useMultiSite();
  const {setCrmData, getRegistrationSyncedSite, clearRegistrationSyncedSite} = useCrm();
  const {syncLogin} = useCrmSync();

  const lastSyncedSiteRef = useRef(null);
  const lastSyncedPreferredStoreRef = useRef(null);
  const isSyncingRef = useRef(false);
  const latestRef = useRef({});

  latestRef.current = {customer, setCrmData, syncLogin};

  useEffect(() => {
    if (!isRegistered || !customerId) {
      lastSyncedSiteRef.current = null;
      lastSyncedPreferredStoreRef.current = null;
      isSyncingRef.current = false;

      return;
    }

    const siteId = site?.id;

    if (!customer?.customerId || !siteId) {
      return;
    }

    const registrationSite = getRegistrationSyncedSite();

    if (registrationSite === siteId) {
      clearRegistrationSyncedSite();
      lastSyncedSiteRef.current = siteId;

      return;
    }

    if (isSyncingRef.current) {
      return;
    }

    const preferredStoreId = getPreferredStoreId(customer, siteId);

    if (lastSyncedSiteRef.current === siteId) {
      if (!preferredStoreId || preferredStoreId === lastSyncedPreferredStoreRef.current) {
        return;
      }
    }

    isSyncingRef.current = true;
    lastSyncedSiteRef.current = siteId;

    let cancelled = false;

    const applySyncResult = (result) => {
      if (!result.ok) {
        console.warn('[CRM Site Sync] CRM sync failed:', result.error);
      }

      latestRef.current.setCrmData(result.ok && result.crm ? result.crm : null);
    };

    const runSync = (currentCustomer, storeId) => latestRef.current.syncLogin({
      profile: {
        firstName: currentCustomer.firstName,
        lastName: currentCustomer.lastName,
        email: currentCustomer.email,
      },
      preferredStoreId: storeId ?? undefined,
    });

    void (async () => {
      try {
        const result = await runSync(customer, preferredStoreId);

        if (cancelled) {
          return;
        }

        applySyncResult(result);
        lastSyncedPreferredStoreRef.current = preferredStoreId;

        const retryCustomer = latestRef.current.customer;
        const latestStoreId = getPreferredStoreId(retryCustomer, siteId);

        if (!latestStoreId || latestStoreId === preferredStoreId) {
          return;
        }

        const retryResult = await runSync(retryCustomer, latestStoreId);

        if (cancelled) {
          return;
        }

        applySyncResult(retryResult);
        lastSyncedPreferredStoreRef.current = latestStoreId;
      } catch (error) {
        if (!cancelled) {
          console.error('[CRM Site Sync] CRM sync error:', error.message);
          latestRef.current.setCrmData(null);
        }
      } finally {
        isSyncingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      isSyncingRef.current = false;
    };
  }, [isRegistered, customerId, customer?.customerId, customer?.c_preferredStoreId, site?.id]);
};

export default useCrmSiteSync;
